import { CAREERS_PATH, isCareersPath } from "./careersRoutes";

/**
 * The Staffing area's URL shapes, in one place.
 *
 * The area is FOUR views behind three URL shapes — `staffing/:dateString?` (weekly),
 * `staffing/monthly/:date?` (monthly) and `staffing/people` (the roster) — and every screen that
 * navigates between them, or rebuilds one of them for a different department, has to agree on which
 * segment means what. Spelling the paths out at each call site is how they came to disagree: the
 * department switcher matched only the weekly shape with a date regex, so switching department from
 * Monthly or People silently dropped the user back to the weekly grid.
 */

/** First path segment of the Staffing area. `staffing2` is a separate route and deliberately not here. */
const STAFFING_SEGMENT = "staffing";
/** Static segment that selects the Monthly view; the date after it is optional (`:date?`). */
const MONTHLY_SEGMENT = "monthly";
/** Static segment that selects the People roster. It outranks `:dateString?`, so it is never a date. */
const PEOPLE_SEGMENT = "people";
/**
 * Static segment that pins the phone to the CLASSIC weekly list while the gaps-first shell
 * (`mobile_gaps_view`) is the default landing. Like `people` and `monthly`, React Router ranks it
 * above `:dateString?`, so it is never read as a date.
 */
const CLASSIC_SEGMENT = "classic";
/**
 * A SEPARATE top-level route that also renders the classic mobile list, and is deliberately never
 * forked to the gaps-first shell (`App.tsx`). It is not part of the Staffing area for the purposes of
 * a department switch — but it is classic for the purposes of the chrome, which is why it appears here.
 */
const STAFFING2_SEGMENT = "staffing2";

/** `yyyy-MM-dd` — the only date shape any Staffing route serializes. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The weekly grid, optionally opened on a specific date. */
export const staffingWeeklyPath = (departmentId: string, date?: string): string =>
	date === undefined ? `/${departmentId}/${STAFFING_SEGMENT}` : `/${departmentId}/${STAFFING_SEGMENT}/${date}`;

/** The Monthly grid. The date names the window's first VISIBLE day, not an arbitrary day inside it. */
export const staffingMonthlyPath = (departmentId: string, date?: string): string =>
	date === undefined ? `/${departmentId}/${STAFFING_SEGMENT}/${MONTHLY_SEGMENT}` : `/${departmentId}/${STAFFING_SEGMENT}/${MONTHLY_SEGMENT}/${date}`;

/** The People roster — dateless: its window is local state, not part of the URL. */
export const staffingPeoplePath = (departmentId: string): string => `/${departmentId}/${STAFFING_SEGMENT}/${PEOPLE_SEGMENT}`;

/** The classic mobile weekly list, optionally opened on a specific date. */
export const staffingClassicPath = (departmentId: string, date?: string): string =>
	date === undefined ? `/${departmentId}/${STAFFING_SEGMENT}/${CLASSIC_SEGMENT}` : `/${departmentId}/${STAFFING_SEGMENT}/${CLASSIC_SEGMENT}/${date}`;

/**
 * Whether this URL is one of the two that render the CLASSIC mobile list no matter what the user's
 * remembered view is — `staffing/classic/:dateString?` and `staffing2/:dateString?`.
 *
 * The gaps-first chrome is session-scoped: the bottom bar stands in for the hamburger on every screen
 * for as long as a user is in that view. These two routes are the exception, because they are pinned to
 * classic in the route table itself rather than by the preference. Without this, a head nurse whose
 * remembered view is gaps could open either URL — a shared link, or a department switch, which
 * deliberately preserves the `classic` segment while the PREFERENCE is per-department — and get
 * classic's list under the gaps bar, with no hamburger and no slot on the bar lit.
 */
export const isClassicStaffingPath = (pathname: string): boolean => {
	const [, , area, view] = pathname.split("/");
	return area === STAFFING2_SEGMENT || (area === STAFFING_SEGMENT && view === CLASSIC_SEGMENT);
};

/**
 * Where a classic mobile screen goes when the user picks a different day — the same route it is on.
 *
 * The classic screen used to spell `/{dept}/staffing/{date}` out, which is the DEFAULT staffing URL and
 * therefore the one the gaps-first switch owns. Changing the day on `staffing/classic` (or being moved
 * off a stale date by the screen's own out-of-range redirect) then handed the user to whichever view
 * their preference selects — so a classic screen could navigate itself into the gaps shell.
 */
export const staffingDatePathForRoute = (pathname: string, departmentId: string, date: string): string => {
	const [, , area, view] = pathname.split("/");
	if (area === STAFFING2_SEGMENT) return `/${departmentId}/${STAFFING2_SEGMENT}/${date}`;
	if (area === STAFFING_SEGMENT && view === CLASSIC_SEGMENT) return staffingClassicPath(departmentId, date);
	return staffingWeeklyPath(departmentId, date);
};

/** The Day view. Its own top-level route, not a Staffing sub-path. */
export const daySchedulePath = (departmentId: string, date: string): string => `/${departmentId}/day-schedule/${date}`;

/**
 * Where a department switch should land, given where the user currently is.
 *
 * Keeps BOTH the view and the date the user is looking at. This used to navigate to the bare
 * `/staffing` route, and the grid then opened on the first week the server returned — which is
 * `department_data_history_weeks` before today, not the week being read. Comparing one week across
 * facilities is the most-repeated interaction an area manager has, and every switch lost their place.
 *
 * Reads the path by SEGMENT rather than by matching a date pattern, so a view is preserved because it
 * was recognised, not because its URL happened to end in a date — the date-regex version silently
 * dropped Monthly and People, the two views whose URLs it could not match.
 *
 * Anywhere outside the Staffing area (the Day view included) deliberately lands on the weekly grid:
 * the switcher's contract is "same view, same date, other department" within Staffing, and only the
 * Staffing grid resolves a date its new department's window does not cover (see `StaffingWrapper`).
 *
 * Careers is the one area outside Staffing that a switch stays in: its URL carries no department (the page
 * reads the chosen one), so the Careers page simply reloads for the new department. A candidate profile
 * lands back on the page, because that candidate belongs to the department just left.
 */
export const resolveDepartmentSwitchDestination = (pathname: string, departmentId: string): string => {
	if (isCareersPath(pathname)) return CAREERS_PATH;
	// ["", departmentId, area, ...rest] — a leading "/" always yields an empty first element.
	const [, , area, viewOrDate, subViewDate] = pathname.split("/");
	const datedSubView = subViewDate !== undefined && ISO_DATE_PATTERN.test(subViewDate) ? subViewDate : undefined;
	if (area !== STAFFING_SEGMENT) return staffingWeeklyPath(departmentId);
	if (viewOrDate === PEOPLE_SEGMENT) return staffingPeoplePath(departmentId);
	if (viewOrDate === MONTHLY_SEGMENT) return staffingMonthlyPath(departmentId, datedSubView);
	// A head nurse who opted OUT of the gaps-first shell keeps classic across a department switch: the
	// switcher's contract is "same view, other department", and the view they chose is this one.
	if (viewOrDate === CLASSIC_SEGMENT) return staffingClassicPath(departmentId, datedSubView);
	return staffingWeeklyPath(departmentId, viewOrDate !== undefined && ISO_DATE_PATTERN.test(viewOrDate) ? viewOrDate : undefined);
};
